import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

import { MaterialCombobox } from '@/components/form/inputs/MaterialCombobox';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { SectionHeader, FieldRow } from './TraceEditPrimitives';
import { flattenIsoTree } from '@/features/traceability/data/isoMaterialTree';

import type { Material } from '@/features/traceability/type/Material';
import type { MaterialLot } from '@/features/traceability/type/MaterialLot';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';

type Props = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  materials: Material[];
  traceabilityApiClient: TraceabilityApiClient | null;
};

export function TraceEditMaterialSection({ values, onChange, materials, traceabilityApiClient }: Props) {
  const { t } = useTranslation();
  const isoOptions = useMemo(() => flattenIsoTree(t), [t]);

  const selectedMaterial = (values['material'] as Material | null) ?? null;
  const selectedLot = (values['materialLot'] as MaterialLot | null) ?? null;

  const [lots, setLots] = useState<MaterialLot[]>([]);

  useEffect(() => {
    if (!traceabilityApiClient || !selectedMaterial?.id) {
      setLots([]);
      return;
    }
    const ac = new AbortController();
    traceabilityApiClient
      .materialLotReadForMaterial({ materialId: selectedMaterial.id }, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        if (res.ok && res.data?.lots) {
          setLots(res.data.lots);
        } else {
          setLots([]);
        }
      })
      .catch((err: unknown) => {
        if (!ac.signal.aborted) console.error(err);
        setLots([]);
      });
    return () => ac.abort();
  }, [traceabilityApiClient, selectedMaterial?.id]);

  const handleSelectMaterial = (id: number | null) => {
    if (id === null) {
      onChange('material', null);
      onChange('materialLot', null);
      return;
    }
    const mat = materials.find((m) => m.id === id) ?? null;
    onChange('material', mat);
    onChange('materialLot', null);
  };

  const handleSelectLot = (val: string | null) => {
    if (!val) {
      onChange('materialLot', null);
      return;
    }
    const lotId = Number(val);
    const lot = lots.find((l) => l.id === lotId) ?? null;
    onChange('materialLot', lot);
  };

  const lotSelectValue = selectedLot?.id !== undefined ? String(selectedLot.id) : '';
  // Radix refuses an empty item value: a lot without an id cannot be picked.
  const lotOptions = lots.flatMap((lot) =>
    lot.id === undefined
      ? []
      : [
          {
            value: String(lot.id),
            label:
              lot.supplier && lot.lotNumber
                ? `${lot.supplier} · ${lot.lotNumber}`
                : (lot.lotNumber ?? lot.supplier ?? String(lot.id)),
          },
        ]
  );

  return (
    <div>
      <SectionHeader title={t('toolView.advanced.sections.material')} />
      <FieldRow>
        <MaterialCombobox
          materials={materials}
          selectedId={selectedMaterial?.id ?? null}
          onSelect={handleSelectMaterial}
          placeholder={t('toolView.advanced.detail.materialPlaceholder')}
          searchPlaceholder={t('toolView.advanced.detail.materialSearchPlaceholder')}
          noResults={t('toolView.advanced.detail.materialNoResults')}
          clearLabel={t('common.clearSelection')}
          isoOptions={isoOptions}
        />
      </FieldRow>
      <FieldRow>
        <SimpleSelect
          value={lotSelectValue}
          onChange={handleSelectLot}
          disabled={!selectedMaterial || lots.length === 0}
          aria-label={t('toolView.advanced.detail.materialLotPlaceholder')}
          options={lotOptions}
          placeholder={
            !selectedMaterial
              ? t('toolView.advanced.detail.materialLotSelectFirst')
              : t('toolView.advanced.detail.materialLotNoLots')
          }
          noneLabel={
            selectedMaterial && lots.length > 0
              ? t('toolView.advanced.detail.materialLotPlaceholder')
              : undefined
          }
        />
      </FieldRow>
    </div>
  );
}
